import { useCallback, useState } from 'react';

import {
  useCreateAnalyticalDatasetMutation,
  type CreatedAnalyticalDataset,
} from '../lib/api/useCreateAnalyticalDatasetMutation';

interface UseCreateAnalyticalDatasetDialogArgs {
  projectId: string;
  onClose: () => void;
  /** Fired with the created profile before close so the caller can route
   *  into the new dataset's detail page. */
  onCreated: (profile: CreatedAnalyticalDataset) => void;
}

export interface UseCreateAnalyticalDatasetDialogResult {
  name: string;
  onChangeName: (id: string, value: string) => void;
  onSubmit: () => void;
  closeDialog: () => void;
  isPending: boolean;
  submittingError: { message: string } | undefined;
  isValidToSubmit: boolean;
}

/**
 * Behaviour for the inline analytical-dataset create dialog: name entry,
 * the create mutation, and the success → navigate handoff. Intl-free so the
 * view owns copy and this stays unit-testable without rendering the dialog.
 */
export const useCreateAnalyticalDatasetDialog = ({
  projectId,
  onClose,
  onCreated,
}: UseCreateAnalyticalDatasetDialogArgs): UseCreateAnalyticalDatasetDialogResult => {
  const [name, setName] = useState('');
  const { mutate, isPending, error, reset } = useCreateAnalyticalDatasetMutation();

  const onChangeName = useCallback((_id: string, value: string) => {
    setName(value);
  }, []);

  const onSubmit = useCallback(() => {
    const trimmed = name.trim();
    if (!trimmed) return;
    mutate(
      { projectId, name: trimmed },
      {
        onSuccess: (response) => {
          onCreated(response.data);
          onClose();
        },
      }
    );
  }, [projectId, name, mutate, onCreated, onClose]);

  const closeDialog = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return {
    name,
    onChangeName,
    onSubmit,
    closeDialog,
    isPending,
    submittingError: error ? { message: error.message } : undefined,
    isValidToSubmit: Boolean(name.trim()),
  };
};
